// ../../react-shim.js
import React from "react";

// src/use-boolean.ts
import { useCallback, useState } from "react";
var useBoolean = (initialState = false) => {
  const [value, setValue] = useState(initialState);
  const on = useCallback(() => {
    setValue(true);
  }, []);
  const off = useCallback(() => {
    setValue(false);
  }, []);
  const toggle = useCallback(() => {
    setValue((prev) => !prev);
  }, []);
  const setState = useCallback((specificValue) => {
    setValue(specificValue);
  }, []);
  return [value, { on, off, toggle, setState }];
};

// src/use-callback-ref.ts
import React3 from "react";

// src/use-safe-layout-effect.ts
import React2 from "react";
import { isBrowser } from "@nature-ui/utils";
var useSafeLayoutEffect = isBrowser ? React2.useLayoutEffect : React2.useEffect;

// src/use-callback-ref.ts
function useCallbackRef(fn, deps = []) {
  const ref = React3.useRef(fn);
  useSafeLayoutEffect(() => {
    ref.current = fn;
  });
  return React3.useCallback((...args) => {
    var _a;
    return (_a = ref.current) == null ? void 0 : _a.call(ref, ...args);
  }, deps);
}

// src/use-clipboard.ts
import React4 from "react";
import copy from "copy-to-clipboard";
var useClipboard = (text, timeout = 1500) => {
  const [copied, setCopied] = React4.useState(false);
  const onCopy = React4.useCallback(() => {
    const didCopy = copy(text);
    setCopied(didCopy);
  }, [text]);
  React4.useEffect(() => {
    let timeoutId = null;
    if (copied) {
      timeoutId = window.setTimeout(() => {
        setCopied(false);
      }, timeout);
    }
    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [timeout, copied]);
  return { value: text, onCopy, copied };
};

// src/use-controllable.ts
import { runIfFn, warn } from "@nature-ui/utils";
import React5 from "react";
var useControllableProp = (prop, state) => {
  const { current: isControlled } = React5.useRef(prop !== void 0);
  const value = isControlled && typeof prop !== "undefined" ? prop : state;
  return [isControlled, value];
};
var defaultPropsMap = {
  value: "value",
  defaultValue: "defaultValue",
  onChange: "onChange"
};
var useControllableState = (props) => {
  const {
    value: valueProp,
    defaultValue,
    onChange,
    shouldUpdate = () => true,
    name = "Component",
    propsMap = defaultPropsMap
  } = props;
  const [valueState, setValue] = React5.useState(defaultValue);
  const { current: isControlled } = React5.useRef(valueProp !== void 0);
  React5.useEffect(() => {
    const nextIsControlled = valueProp !== void 0;
    const nextMode = nextIsControlled ? "a controlled" : "an uncontrolled";
    const mode = isControlled ? "a controlled" : "an uncontrolled";
    warn({
      condition: isControlled !== nextIsControlled,
      message: `Warning: ${name} is changing from ${mode} from ${nextMode} componentComponent should not switch from controlled to uncontrolled (or vice versa). Use the '${propsMap.value}' with an '${propsMap.onChange}' handler. If you want an uncontrolled component, remove the ${propsMap.value} prop and use '${propsMap.defaultValue}' instead. More info: https://fb.me/react-controlled-components`
    });
  }, [valueProp, isControlled, name]);
  const { current: _defaultValue } = React5.useRef(defaultValue);
  React5.useEffect(() => {
    warn({
      condition: _defaultValue !== defaultValue,
      message: `Warning: A component is changing the default value of an uncontrolled ${name} after being initialized. To suppress this warning opt to use a controlled ${name}.`
    });
  }, [JSON.stringify(defaultValue)]);
  const value = isControlled ? valueProp : valueState;
  const updateValue = React5.useCallback(
    (next) => {
      const nextValue = runIfFn(next, value);
      const shouldUpdateState = shouldUpdate(value, nextValue);
      if (!shouldUpdateState)
        return;
      if (!isControlled) {
        setValue(next);
      }
      onChange == null ? void 0 : onChange(nextValue);
    },
    [onChange, shouldUpdate, isControlled, value]
  );
  return [value, updateValue];
};

// src/use-disclosure.ts
import { callAllHandler } from "@nature-ui/utils";
import React6 from "react";

// src/use-id.ts
import { useId as useUID } from "@reach/auto-id";
var generatePrefix = (prefix, id) => {
  return `${prefix}-${id}`;
};
var useId = (idProp, prefix) => {
  const uuid = useUID();
  const id = idProp ?? uuid;
  const result = prefix ? generatePrefix(prefix, id) : id;
  return result;
};
var useIds = (idProp, ...prefixes) => {
  const id = useId(idProp);
  return prefixes.map((prefix) => generatePrefix(prefix, id));
};

// src/use-disclosure.ts
var useDisclosure = (props = {}) => {
  const {
    onClose: onCloseProp,
    onOpen: onOpenProp,
    isOpen: isOpenProp,
    id: idProp
  } = props;
  const [isOpenState, setIsOpen] = React6.useState(props.defaultIsOpen || false);
  const [isControlled, isOpen] = useControllableProp(isOpenProp, isOpenState);
  const id = useId(idProp, "disclosure");
  const onClose = React6.useCallback(() => {
    if (!isControlled) {
      setIsOpen(false);
    }
    onCloseProp == null ? void 0 : onCloseProp();
  }, [isControlled, onCloseProp]);
  const onOpen = React6.useCallback(() => {
    if (!isControlled) {
      setIsOpen(true);
    }
    onOpenProp == null ? void 0 : onOpenProp();
  }, [isControlled, onOpenProp]);
  const onToggle = React6.useCallback(() => {
    const action = isOpen ? onClose : onOpen;
    action();
  }, [isOpen, onOpen, onClose]);
  return {
    isOpen: Boolean(isOpen),
    onOpen,
    onClose,
    onToggle,
    isControlled,
    getButtonProps: (_props = {}) => ({
      ..._props,
      "aria-expanded": "true",
      "aria-controls": id,
      onClick: callAllHandler(_props.onClick, onToggle)
    }),
    getDisclosureProps: (_props = {}) => ({
      ..._props,
      hidden: !isOpen,
      id
    })
  };
};

// src/use-event-listener.ts
import { runIfFn as runIfFn2 } from "@nature-ui/utils";
import { useEffect } from "react";
var useEventListener = (event, handler, env, options) => {
  const listener = useCallbackRef(handler);
  useEffect(() => {
    const node = runIfFn2(env) ?? document;
    node.addEventListener(event, listener, options);
    return () => {
      node.removeEventListener(event, listener, options);
    };
  }, [event, env, options, listener]);
  return () => {
    const node = runIfFn2(env) ?? document;
    node.removeEventListener(event, listener, options);
  };
};

// src/use-force-update.ts
import React7 from "react";
var useForceUpdate = () => {
  const [count, setCount] = React7.useState(0);
  const forceUpdate = React7.useCallback(() => {
    setCount(count + 1);
  }, [count]);
  return forceUpdate;
};

// src/use-interval.ts
import React9 from "react";

// src/use-latest-ref.ts
import React8 from "react";
var useLatestRef = (value) => {
  const ref = React8.useRef(value);
  React8.useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
};

// src/use-interval.ts
var useInterval = (callback, delay) => {
  const savedCallback = useLatestRef(callback);
  React9.useEffect(() => {
    const tick = () => {
      var _a;
      (_a = savedCallback.current) == null ? void 0 : _a.call(savedCallback);
    };
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay, savedCallback]);
};

// src/use-timeout.ts
import React10 from "react";
var useTimeout = (callback, delay) => {
  const saveCallback = useLatestRef(callback);
  React10.useEffect(() => {
    if (delay === null)
      return;
    const timeoutId = setTimeout(() => {
      var _a;
      (_a = saveCallback.current) == null ? void 0 : _a.call(saveCallback);
    }, delay);
    return () => clearTimeout(timeoutId);
  }, [delay, saveCallback]);
};

// src/use-unmount-effect.ts
import React11 from "react";
function useUnmountEffect(fn, deps = []) {
  return React11.useEffect(
    () => () => fn(),
    deps
  );
}

// src/use-update-effect.ts
import React12 from "react";
var useUpdateEffect = (effect, deps) => {
  const mounted = React12.useRef(false);
  React12.useEffect(() => {
    if (mounted.current) {
      return effect();
    }
    mounted.current = true;
    return void 0;
  }, deps);
  return mounted.current;
};
export {
  useBoolean,
  useCallbackRef,
  useClipboard,
  useControllableProp,
  useControllableState,
  useDisclosure,
  useEventListener,
  useForceUpdate,
  useId,
  useIds,
  useInterval,
  useLatestRef,
  useSafeLayoutEffect,
  useTimeout,
  useUnmountEffect,
  useUpdateEffect
};
